import React, { useEffect, useRef, useState } from 'react';
import { signIn } from '../services/auth';
import { fetchProvisioningStatus } from '../services/api';
import { PublicHeader, PublicFooter } from './LandingPage';
import { LEGAL } from '../legalInfo';
import { Loading } from '../components/ui';

// Where Stripe Checkout returns to. Polls until the tenant has been
// provisioned, then sends the user into the app.
const POLL_MS = 2000;
const GIVE_UP_MS = 120000;

export default function WelcomePage() {
  // provisioning | active | failed | unknown | not_started | slow
  const [status, setStatus] = useState('provisioning');
  const [tenant, setTenant] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const timer = useRef(null);
  // Whether the API has ever answered tenant_not_found. Distinguishes "the
  // webhook never ran" from "provisioning is taking longer than expected",
  // which need different things from the reader.
  const sawNotFound = useRef(false);

  useEffect(() => {
    let email = '';
    try { email = localStorage.getItem('ydyai_signup_email') || ''; } catch { email = ''; }
    if (!email) { setStatus('unknown'); return undefined; }

    let stopped = false;
    const poll = async () => {
      try {
        const data = await fetchProvisioningStatus(email);
        if (stopped) return;
        if (data?.ok && data.status) {
          setTenant(data);
          setStatus(data.status);
          if (data.ready) { stopped = true; clearInterval(timer.current); }
        } else if (data?.error === 'tenant_not_found') {
          // No tenant exists yet. Normal for the first few seconds - Stripe's
          // webhook is what creates it - but if it is STILL missing once the
          // grace period is up, provisioning never started, and saying
          // "almost done" at that point is just untrue.
          sawNotFound.current = true;
        }
      } catch { /* transient - the next tick retries */ }
    };
    poll();
    timer.current = setInterval(() => { setElapsed((e) => e + POLL_MS / 1000); poll(); }, POLL_MS);
    const giveUp = setTimeout(() => {
      stopped = true;
      clearInterval(timer.current);
      // Stopping the spinner without changing the message left the page
      // claiming it was still working when it had given up entirely.
      setStatus((current) => (current === 'provisioning' ? (sawNotFound.current ? 'not_started' : 'slow') : current));
    }, GIVE_UP_MS);
    return () => { stopped = true; clearInterval(timer.current); clearTimeout(giveUp); };
  }, []);

  const ready = status === 'active' || tenant?.ready;

  return (
    <div className="public">
      <PublicHeader showLogin={false} />
      <main className="public-main page-narrow">
        <div className="card" style={{ textAlign: 'center' }}>
          {ready ? (
            <>
              <div className="welcome-tick">✓</div>
              <h1>You're all set</h1>
              <p className="sub">Your workspace{tenant?.domain ? ` (${tenant.domain})` : ''} is ready.</p>
              {/* The step nobody was told about.
                  Signing in needs the address the payment was made with,
                  because that is the one the workspace was created for and the
                  one the directory knows. Anyone without a Microsoft account
                  gets a six-digit code by email instead of a password prompt,
                  which is unexpected enough to be worth saying in advance -
                  otherwise a code arriving looks like something has gone
                  wrong. */}
              <p className="sub">
                Sign in with <strong>the email address you paid with</strong>. If you don't have a
                Microsoft account, we'll email you a code instead of asking for a password.
              </p>
              <button type="button" className="btn" onClick={signIn}>Go to your workspace</button>
            </>
          ) : status === 'failed' ? (
            <>
              <h1>Something went wrong</h1>
              <p className="sub">We couldn't finish setting up your workspace. Please contact support.</p>
            </>
          ) : status === 'not_started' ? (
            <>
              <h1>Your payment went through</h1>
              <p className="sub">
                Setting up your workspace hasn't started yet. Your payment is safe and nothing
                needs paying again — this is on our side, and we can finish it from the payment
                record. Please contact support and quote the email you signed up with.
              </p>
            </>
          ) : status === 'slow' ? (
            <>
              <h1>Still working on it</h1>
              <p className="sub">
                Setting up is taking longer than usual. Your payment went through and your
                workspace will be ready shortly — reload this page in a few minutes, or contact
                support if it stays like this.
              </p>
            </>
          ) : status === 'unknown' ? (
            <>
              <h1>Almost there</h1>
              <p className="sub">
                We couldn't find your sign-up session. If you have just paid, check your email or contact support.
              </p>
            </>
          ) : (
            <>
              <h1>Setting up your workspace…</h1>
              <p className="sub">
                Provisioning your storage, AI and settings. This usually takes under a minute
                {elapsed > 20 ? ' — almost done' : ''}.
              </p>
              <Loading text="Provisioning…" />
            </>
          )}
          {/* Shown in every state, including success.
              The states that mention support are the ones where we already
              know something is wrong - but the worst case is a buyer who sees
              "You're all set", cannot sign in, and has no way to reach anyone.
              Money taken, no access, no route out, and the next step is a
              chargeback. */}
          <p className="sub welcome-help">
            Trouble signing in? Email <a href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a> and
            quote the address you paid with.
          </p>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
